<template>
    <div class="h-full flex flex-col">
        <div class="bg-white py-7 container border-b flex border-slate-50">
            <SearchGlobal
                ref="search"
                :placeholder="minMD ? $t(`search.searchBar.input.all`) : $t(`search.searchBar.input.all_mobile`)"
                :model-value="phrase"
                enable-recent-search
                :university-id="filters.university_id"
                with-navigation
                :icon="undefined"
                :no-indicator="false"
                close-on-select
                label-field="phrase"
                data-testid="global-search-bar"
                @clear="clearPhrase"
                @keydown.enter="loadSearch"
                @input="loadSearch"
            />
            <Btn
                class="ml-4 lg:!hidden"
                variant="outlined"
                color="primary"
                color-on-active
                left-icon="filters"
                @click="toggleFilters"
            />
        </div>
        <div class="lg:flex grow bg-white container">
            <SearchFiltersWrapper
                v-model="filters"
                :fullscreen="!minLG"
                :fullscreen-opened="displayFilters || minLG"
                class="lg:relative lg:w-64 lg:mt-12 lg:mb-14 lg:mr-8"
                @update:fullscreen-opened="displayFilters = false"
            />
            <div class="flex-1 py-6 min-w-0">
                <div
                    v-if="hasOnlySecondaryDocuments"
                    class="flex justify-center items-center bg-blue-300 text-slate-400 text-sm px-4 py-3 gap-2 mt-2 rounded font-normal rounded-2xl leading-5 mb-3"
                >
                    <span><i class="h-6 w-6 icon icon-info text-slate-400 mt-1"></i></span>
                    <p class="flex-1 text-sm text-slate-400 font-medium">
                        {{ $t('search.document_results.none_found') }}
                    </p>
                </div>
                <CourseResults
                    class="mb-6"
                    :searched-phrase="phrase"
                    :university-ids="filters.university_id ? [filters.university_id] : []"
                    :course-id="filters.course_id"
                    :term="phrase"
                    :institution-type="filters.institution_type"
                    @search-finished="trackSearch"
                />
                <DocumentResults
                    v-model:sort="sort"
                    :filters="filters"
                    :term="phrase"
                    @search-finished="trackSearch"
                    @has-only-secondary-results="hasResults"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import SearchGlobal from '@/components/SearchGlobal';
import { GtmEventName, pushGtmEvent } from '@/utils/gtmUtils';
import { trackImpression } from '@/modules/tracking';
import { useScreenSize } from '@/composables/screenSize';
import { usePageMeta } from '@/composables/pageMeta';
import { StudyMaterialsSortType } from '@/api/gateway/legacy-api/search';
import { type SearchFilters as SearchFiltersType, TrackingContentType } from './SearchIndex';
import CourseResults from './CourseResults.vue';
import DocumentResults from './DocumentResults.vue';
import SearchFiltersWrapper from './SearchFiltersWrapper.vue';

export default defineComponent({
    name: 'SearchIndex',
    components: {
        CourseResults,
        SearchGlobal,
        DocumentResults,
        SearchFiltersWrapper,
    },
    setup() {
        usePageMeta({
            title: 'tags:search.title',
            description: 'tags:search.description',
            image: 'tags:search.image',
            index: false,
        });
        const { minMD, minLG } = useScreenSize();
        return {
            minLG,
            minMD,
        };
    },
    data() {
        return {
            displayFilters: false,
            page: 1,
            hasOnlySecondaryDocuments: false,
        };
    },
    computed: {
        ...mapGetters({
            params: 'search/searchParams',
            isLoggedIn: 'auth/isLoggedIn',
            recentSearches: 'search/recentSearches',
        }),
        phrase: {
            get(): string | undefined {
                return this.params.phrase ? String(this.params.phrase) : undefined;
            },
            set(phrase: string): void {
                this.$store.dispatch('search/applyParams', {
                    phrase,
                    ...this.filters,
                });
            },
        },
        filters: {
            get(): SearchFiltersType {
                return {
                    university_id: this.params.university_id ? Number(this.params.university_id) : undefined,
                    institution_type:
                        this.params.institution_type === undefined ? 1 : Number(this.params.institution_type),
                    course_id: this.params.course_id ? Number(this.params.course_id) : undefined,
                    type_ids: this.params.type_ids
                        ? Object.values(this.params.type_ids).map((id) => Number(id))
                        : undefined,
                    language_id: this.params.language_id ? Number(this.params.language_id) : 0,
                    has_ai_content: this.params.has_ai_content ? Boolean(this.params.has_ai_content) : undefined,
                    min_academic_year: this.params.min_academic_year
                        ? Number(this.params.min_academic_year)
                        : undefined,
                    max_academic_year: this.params.max_academic_year
                        ? Number(this.params.max_academic_year)
                        : undefined,
                };
            },
            set(filters: SearchFiltersType): void {
                this.$store.dispatch('search/applyParams', {
                    phrase: this.phrase,
                    ...filters,
                });
            },
        },
        sort: {
            get(): StudyMaterialsSortType {
                return (
                    (this.params?.sort_type_documents as StudyMaterialsSortType) ?? StudyMaterialsSortType.VoteRating
                );
            },
            set(sort: StudyMaterialsSortType): void {
                this.$store.dispatch('search/applyParams', {
                    sort_type_documents: sort,
                });
            },
        },
    },
    mounted() {
        this.$store.dispatch('ui/enableSearchTopBar', false);
        this.loadSearch();
    },
    unmounted() {
        if (this.isLoggedIn) {
            this.$store.dispatch('ui/enableSearchTopBar', true);
        }
    },
    methods: {
        pushGtmEvent,
        toggleFilters() {
            this.displayFilters = !this.displayFilters;
        },
        clearPhrase() {
            this.phrase = undefined;
        },
        loadSearch() {
            if (
                this.phrase &&
                !this.recentSearches.find((search: { phrase: string }) => search.phrase === this.phrase)
            ) {
                this.$store.dispatch('search/push', {
                    ...this.params,
                });
                this.pushGtmEvent(GtmEventName.SEARCH);
            }
        },
        hasResults(val: boolean) {
            this.hasOnlySecondaryDocuments = val;
        },
        trackSearch(count: number, type: TrackingContentType) {
            const searchUuid = Math.random().toString(36).substring(2, 11);
            trackImpression('Search', {
                query_phrase: this.phrase,
                referrer_url: document.referrer,
                nb_results: count,
                uni_filter: this.filters?.university_id ?? null,
                course_filter: this.filters.course_id,
                content_type: type,
                type_ids: type === TrackingContentType.DocumentsFlashcards ? this.filters.type_ids : undefined,
                sort_type: type === TrackingContentType.DocumentsFlashcards ? this.sort : undefined,
                is_active_search: true,
                search_uuid: searchUuid,
            });
        },
    },
});
</script>

<style scoped lang="scss">
.selector {
    &.default {
        :deep() {
            .icon-search {
                @apply mt-0 bg-black z-[9] #{!important};
            }
            .vs__dropdown-toggle {
                @apply py-2 lg:py-3 px-4;
            }
            &.overlayed {
                :deep(.vs__dropdown-menu) {
                    @apply block;
                }
            }

            .vs__selected-options {
                @apply mr-0;
                .icon-search {
                    @apply order-1;
                }
                .vs__selected + input {
                    @apply md:pl-0;
                }
            }
        }
    }
    &.custom-background {
        :deep(.v-select:not(.vs--open):not(.vs--loading) .vs__search) {
            opacity: 1;
        }
        :deep(.v-select:not(.vs--open) .vs__dropdown-toggle) {
            @apply border-slate-100 bg-slate-50;
        }
    }
}
</style>
